import React, { Suspense, useCallback, useState } from 'react';
import cx from 'classnames';

import { forTemplate } from '@trello/i18n';
import { PowerUpIcon } from '@trello/nachos/icons/power-up';
import { Button } from '@trello/nachos/button';
import { usePopover, Popover } from '@trello/nachos/popover';
import { PluginHeaderButtonTestIds } from '@trello/test-ids';
import { Spinner } from '@trello/nachos/spinner';

import styles from './PluginListButton.less';
import { Board } from './PluginHeaderButton.types';
import { usePluginListButtonQuery } from './PluginListButtonQuery.generated';
import { useLazyComponent } from '@trello/use-lazy-component';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';

const format = forTemplate('board_header_pup_dropdown');

interface PluginListButtonProps {
  openDirectory: () => object;
  openEnabled: () => object;
  openPluginSettings: () => object;
  board: Board;
  container: Element;
}

export const PluginListButton: React.FunctionComponent<PluginListButtonProps> = ({
  openDirectory,
  openEnabled,
  openPluginSettings,
  board,
  container,
}: PluginListButtonProps) => {
  const PluginListPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "plugin-list-popover" */ 'app/src/components/PluginHeaderButton'
      ),
    {
      namedImport: 'PluginListPopover',
    },
  );

  const numPlugins = board?.powerUpsCount();
  const idBoard = board.id;
  const idOrganization = board?.attributes?.idOrganization;
  const { data } = usePluginListButtonQuery({
    variables: { idBoard },
  });
  const backgroundBrightness = data?.board?.prefs?.backgroundBrightness;

  const [traceId, setTraceId] = useState('');

  const openDirectoryCallback = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'pluginListAddPowerUpsButton',
      source: 'powerUpHubPopover',
      containers: formatContainers({
        idBoard,
        idOrganization,
      }),
    });
    openDirectory();
  }, [openDirectory, idOrganization, idBoard]);

  const startTrace = useCallback(() => {
    setTraceId(
      Analytics.startTask({
        taskName: 'view-power-up-hub',
        source: 'powerUpHubPopover',
      }),
    );
  }, []);

  const { triggerRef, toggle, popoverProps } = usePopover<HTMLButtonElement>({
    onShow: startTrace,
  });

  const openPluginList = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'pluginListButton',
      source: 'boardScreen',
      containers: formatContainers({
        idBoard,
        idOrganization,
      }),
    });
    Analytics.sendScreenEvent({
      name: 'powerUpsMenuInlineDialog',
    });
    toggle();
  }, [toggle, idBoard, idOrganization]);

  return (
    <div>
      <Button
        data-test-id={PluginHeaderButtonTestIds.HeaderButton}
        className={styles.pluginDropdownButton}
        ref={triggerRef}
        appearance={
          backgroundBrightness === 'dark' ? 'transparent' : 'transparent-dark'
        }
        iconBefore={<PowerUpIcon size="small" />}
        onClick={openPluginList}
      >
        {format('power-ups')}
      </Button>
      <Popover
        dangerous_className={styles.removePopoverPadding}
        title={format('power-ups')}
        {...popoverProps}
      >
        <Suspense fallback={<Spinner centered />}>
          <PluginListPopover
            board={board}
            container={container}
            openDirectory={openDirectory}
            openEnabled={openEnabled}
            openPluginSettings={openPluginSettings}
            toggle={toggle}
            traceId={traceId}
          />
          {board.editable() ? (
            <div
              className={cx(
                styles.footerBox,
                numPlugins === 0 && styles.noPlugins,
              )}
            >
              <Button
                appearance={'primary'}
                shouldFitContainer={true}
                onClick={openDirectoryCallback}
              >
                {format('add-power-ups')}
              </Button>
            </div>
          ) : null}
        </Suspense>
      </Popover>
    </div>
  );
};
